import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { topics } from '@/mocks/topics';
import { articles } from '@/mocks/articles';

export default function TopicPage() {
  const { id } = useParams();
  const topic = useMemo(() => topics.find((t) => t.id === id), [id]);

  useEffect(() => {
    document.title = topic
      ? `${topic.title} - 资讯专题 - 姐姐的朋友影视内容站`
      : '资讯专题不存在 - 姐姐的朋友影视内容站';
  }, [topic]);

  const topicArticles = useMemo(
    () => (topic ? articles.filter((a) => topic.articleIds.includes(a.id)) : []),
    [topic],
  );
  const relatedTopics = useMemo(
    () => (topic ? topics.filter((t) => topic.relatedTopicIds.includes(t.id)) : []),
    [topic],
  );

  if (!topic) {
    return (
      <div className="pt-16 md:pt-[72px]">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="font-heading font-bold text-xl text-foreground-900 mb-2">资讯专题不存在或已删除</h1>
          <p className="text-sm text-foreground-500 mb-6">该专题可能已被移除，返回首页浏览其他内容</p>
          <Link
            to="/"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden bg-primary-950">
        <img
          src={topic.cover}
          alt={`专题：${topic.title}`}
          title={`${topic.title} 资讯专题`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/55 to-primary-950/20" />
        <div className="absolute inset-0 max-w-[1280px] mx-auto px-4 md:px-8 flex flex-col justify-end pb-8 md:pb-12">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '资讯专题' }, { label: topic.title }]} />
          <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-semibold mt-4 mb-3">
            <i className="ri-article-line" />
            专题聚焦
          </span>
          <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50 max-w-4xl leading-tight">
            {topic.title}
          </h1>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <p className="text-sm md:text-base text-foreground-700 leading-loose max-w-4xl mb-10">{topic.intro}</p>

        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-6">
          <span className="w-1.5 h-6 rounded-full bg-accent-500" />
          关联资讯
        </h2>

        {topicArticles.length > 0 ? (
          <div className="flex flex-col gap-4 md:gap-6">
            {topicArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group flex flex-col sm:flex-row gap-4 md:gap-6 p-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-full sm:w-56 md:w-64 shrink-0 aspect-video rounded-md overflow-hidden bg-background-200">
                  <img
                    src={article.cover}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-col justify-center gap-3 min-w-0">
                  <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-time-line" />
                    {article.date}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg bg-background-100 border border-background-200">
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-background-200 text-foreground-400 mb-3">
              <i className="ri-inbox-line text-2xl" />
            </span>
            <p className="text-sm text-foreground-500">暂无相关资讯</p>
          </div>
        )}

        {relatedTopics.length > 0 && (
          <section className="mt-14">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-6">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              相关推荐专题
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {relatedTopics.map((item) => (
                <Link
                  key={item.id}
                  to={`/topic/${item.id}`}
                  className="group relative h-44 md:h-52 rounded-lg overflow-hidden border border-background-200 cursor-pointer"
                >
                  <img
                    src={item.cover}
                    alt={`专题：${item.title}`}
                    title={`${item.title} 资讯专题`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-heading font-semibold text-base md:text-lg text-background-50 leading-snug line-clamp-2">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}